// src/screens/LegalScreen.jsx
// Redesigned with clean, modern UI

import { useState, useMemo, useEffect } from 'react'
import {
  ChevronDown, CheckCircle2, AlertTriangle, Upload, FileText, X, ShieldAlert,
  Lock, Handshake, Download, Check, Clock, RefreshCw, FileEdit, Archive, XCircle,
  Building2, Wallet, Users, ShieldCheck, Scale, ArrowRight, ExternalLink, ArrowLeft,
  Globe, Search, Filter, Star, Briefcase, Languages, MessageCircle, Plus,
  TrendingUp, FileCheck, Calendar, AlertCircle
} from 'lucide-react'
import { getLocalizedLabels } from '../lib/localization'
import AgentModal from '../components/AgentModal'
import { useToast } from '../lib/ToastContext'
import { documentsApi, agentRequestsApi, agentMarketplaceApi } from '../lib/api'
import { supabase } from '../lib/supabase'
import { calculateScore, CATEGORY_LABELS } from '../lib/governanceScore'
import { getSignedUrlByPath } from '../lib/storage'
import { getCountryDocuments, getCountryCategories, getCountryDocumentsList } from '../data/country-documents'
import { getAppMode } from '../lib/mode'
import { getPlans, getCategoryAccess } from '../data/pricing'
import { useAuth } from '../hooks/useAuth'
import { hasSectorAccess, hasAgentAccess } from '../data/pricing'

// ✅ Legal components
import DocumentCard from '../components/legal/DocumentCard'
import DocumentFilters from '../components/legal/DocumentFilters'
import DocumentUpload from '../components/legal/DocumentUpload'
import DocumentPreview from '../components/legal/DocumentPreview'
import { Card, Badge, Button, EmptyState, Spinner, Modal, Tabs } from '../components/ui'

// Status meta for document display
const STATUS_META = {
  uploaded: { label: 'Uploaded', variant: 'primary', icon: Upload },
  pending: { label: 'Processing', variant: 'warning', icon: AlertTriangle },
  missing: { label: 'Missing', variant: 'error', icon: AlertTriangle },
  draft: { label: 'Draft', variant: 'primary', icon: FileEdit },
  awaiting: { label: 'Awaiting Verification', variant: 'warning', icon: Clock },
  verified: { label: 'Verified', variant: 'success', icon: CheckCircle2 },
  rejected: { label: 'Rejected', variant: 'error', icon: XCircle },
  archived: { label: 'Archived', variant: 'default', icon: Archive },
}

const CATEGORY_ICONS = { Building2, Wallet, Users, ShieldCheck, Scale }

function healthColor(score) {
  const numScore = typeof score === 'number' ? score : 0
  if (numScore >= 70) return { bar: 'bg-emerald-500', text: 'text-emerald-700', label: 'Venture-Grade' }
  if (numScore >= 41) return { bar: 'bg-amber-500', text: 'text-amber-700', label: 'Building' }
  return { bar: 'bg-rose-500', text: 'text-rose-700', label: 'Early Stage' }
}

export default function LegalScreen({ 
  country = 'Nigeria', 
  companyName, 
  onAgentRequest, 
  currentPlan = 'growth', 
  onNavigateBilling, 
  companyId, 
  onNavigate 
}) {
  const { company } = useAuth()
  const [docs, setDocs] = useState([])
  const [openCats, setOpenCats] = useState({})
  const [agentTarget, setAgentTarget] = useState(null)
  const [templateModal, setTemplateModal] = useState(null)
  const [signedUrls, setSignedUrls] = useState({})
  const [previewDoc, setPreviewDoc] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedCritical, setSelectedCritical] = useState(false)
  
  // ✅ Agent marketplace states
  const [showAgentMarketplace, setShowAgentMarketplace] = useState(false)
  const [availableAgents, setAvailableAgents] = useState([])
  const [loadingAgents, setLoadingAgents] = useState(false)
  const [selectedAgentForHire, setSelectedAgentForHire] = useState(null)
  
  // ✅ Country-specific document data
  const [countryDocData, setCountryDocData] = useState(null)
  const [countryCategories, setCountryCategories] = useState([])
  const [countryDocumentTemplates, setCountryDocumentTemplates] = useState([])
  
  const { toast } = useToast()
  const regulatoryItems = getLocalizedLabels(country)
  
  // Status options for filters
  const statusOptions = ['missing', 'uploaded', 'pending', 'draft', 'awaiting', 'verified', 'rejected', 'archived']

  // Check if user has agent access
  const hasAgentAccessPlan = hasAgentAccess(currentPlan, country)
  const isWhiteGlove = company?.subscription_tier === 'enterprise'

  // ============================================================
  // LOAD COUNTRY-SPECIFIC DOCUMENTS
  // ============================================================
  useEffect(() => {
    const countryData = getCountryDocuments(country)
    setCountryDocData(countryData)
    setCountryCategories(countryData?.categories || [])
    setCountryDocumentTemplates(countryData?.documents || [])
    
    const initialOpen = {}
    const categories = countryData?.categories || []
    categories.forEach((cat, index) => {
      initialOpen[cat.id] = index < 2
    })
    setOpenCats(initialOpen)
    
  }, [country])

  // ============================================================
  // LOAD USER DOCUMENTS FROM DATABASE
  // ============================================================
  useEffect(() => {
    let mounted = true
    
    if (!companyId) {
      if (countryDocumentTemplates.length > 0) {
        setDocs(countryDocumentTemplates.map((d) => ({ 
          ...d, 
          status: 'missing', 
          fileName: null,
          tier: d.category,
        })))
      }
      return
    }
    
    ;(async () => {
      try {
        const { data, error } = await documentsApi.list(companyId)
        if (!mounted) return
        
        if (!error && data && data.length > 0) {
          const mergedDocs = countryDocumentTemplates.map((template) => {
            const userDoc = data.find(d => d.label === template.label)
            return {
              id: userDoc?.id || template.id,
              label: template.label,
              tier: template.category,
              status: userDoc?.status || 'missing',
              fileName: userDoc?.file_name || null,
              fileUrl: userDoc?.file_url || null,
              critical: template.critical || false,
              rejectReason: userDoc?.reject_reason || null,
            }
          })
          setDocs(mergedDocs)
          
          const urls = {}
          for (const doc of data) {
            if (doc.file_url) {
              try {
                const url = await getSignedUrlByPath(doc.file_url, 60)
                if (url) urls[doc.id] = url
              } catch {}
            }
          }
          if (mounted) setSignedUrls(urls)
        } else {
          setDocs(countryDocumentTemplates.map((d) => ({ 
            ...d, 
            status: 'missing', 
            fileName: null,
            tier: d.category,
          })))
        }
      } catch (err) {
        console.error('Failed to fetch documents:', err)
      }
    })()
    
    return () => { mounted = false }
  }, [companyId, countryDocumentTemplates])

  // ============================================================
  // LOAD AVAILABLE AGENTS
  // ============================================================
  const loadAvailableAgents = async () => {
    setLoadingAgents(true)
    // Simulate for now - replace with actual API
    const mockAgents = [
      { id: '1', name: 'Tunde Bakare', country: 'Nigeria', experience_years: 8, rating: 4.9, specialties: ['Corporate Law', 'Tax', 'SCUML'], languages: ['English', 'Yoruba'], hourly_rate: 15000, currency: 'NGN' },
      { id: '2', name: 'Chidi Okafor', country: 'Nigeria', experience_years: 6, rating: 4.8, specialties: ['CAC Filing', 'Annual Returns', 'Corporate Governance'], languages: ['English', 'Igbo'], hourly_rate: 12000, currency: 'NGN' },
    ]
    setAvailableAgents(mockAgents)
    setLoadingAgents(false)
  }

  // ============================================================
  // HANDLE AGENT SELECTION
  // ============================================================
  const handleHireAgent = async (agent, doc) => {
    if (!doc) {
      toast('Please select a document to assign to the agent.', 'error')
      return
    }
    
    toast(`Agent ${agent.name} assigned to ${doc.label}! (Demo)`, 'success')
    setShowAgentMarketplace(false)
    setSelectedAgentForHire(null)
  }

  // ============================================================
  // GET ACCESSIBLE CATEGORIES BASED ON PLAN
  // ============================================================
  const accessibleCats = useMemo(() => {
    return getCategoryAccess(currentPlan, country)
  }, [currentPlan, country])

  // ============================================================
  // TOGGLE CATEGORY
  // ============================================================
  const toggleCat = (id) => setOpenCats((p) => ({ ...p, [id]: !p[id] }))

  // ============================================================
  // DOCUMENT ACTIONS
  // ============================================================
  const handleUpload = async (docId) => {
    try {
      const doc = docs.find(d => d.id === docId || d.id === `template-${docId}`)
      if (!doc) return
      
      setDocs((prev) => prev.map((d) => 
        (d.id === docId || d.id === `template-${docId}`) 
          ? { ...d, status: 'uploaded', fileName: `${d.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf` } 
          : d
      ))
      
      toast(`Document uploaded: ${doc.label}`, 'success')
    } catch (err) {
      console.error('Upload error:', err)
      toast('Failed to upload document', 'error')
    }
  }

  const handleApproveDraft = async (docId) => {
    try {
      setDocs((prev) => prev.map((d) => d.id === docId ? { ...d, status: 'awaiting' } : d))
      toast('Draft approved and pushed to vault.', 'success')
    } catch (err) {
      console.error('Approve draft error:', err)
      toast('Failed to approve draft', 'error')
    }
  }

  const handleReupload = async (docId) => {
    try {
      setDocs((prev) => prev.map((d) => d.id === docId ? { ...d, status: 'pending', rejectReason: null } : d))
      toast('Document re-uploaded for review.', 'info')
    } catch (err) {
      console.error('Reupload error:', err)
      toast('Failed to re-upload document', 'error')
    }
  }

  const handleRemove = (docId) => {
    setDocs((prev) => prev.map((d) => d.id === docId ? { ...d, status: 'missing', fileName: null } : d))
  }

  // ============================================================
  // AGENT REQUEST
  // ============================================================
  const handleAgentRequest = async (payload) => {
    try {
      onAgentRequest?.(payload)
      toast(`Agent request submitted for: ${payload.docName}`, 'success')
    } catch (err) {
      console.error('Agent request error:', err)
      toast('Failed to submit agent request', 'error')
    }
  }

  // ============================================================
  // FILTER DOCUMENTS
  // ============================================================
  const filterDocs = (docsToFilter) => {
    return docsToFilter.filter(doc => {
      if (searchQuery && !doc.label.toLowerCase().includes(searchQuery.toLowerCase())) return false
      if (selectedCategory && doc.tier !== parseInt(selectedCategory)) return false
      if (selectedStatus && doc.status !== selectedStatus) return false
      if (selectedCritical && !doc.critical) return false
      return true
    })
  }

  // ============================================================
  // CALCULATE HEALTH SCORE
  // ============================================================
  const { healthScore, categoryBreakdown } = useMemo(() => {
    try {
      if (!docs || docs.length === 0) {
        return {
          healthScore: 0,
          categoryBreakdown: countryCategories.map((c) => ({
            key: c.id,
            label: c.name || 'Unknown',
            value: 0,
          })),
        }
      }
      
      const result = calculateScore(docs.map((d) => ({ category: d.tier, status: d.status })))
      
      let score = 0
      if (typeof result === 'number') {
        score = result
      } else if (result && typeof result === 'object') {
        score = result.overall || result.score || 0
      }
      
      const breakdown = countryCategories.map((cat) => {
        const catDocs = docs.filter((d) => d.tier === cat.id)
        const good = catDocs.filter((d) => ['uploaded', 'awaiting', 'verified'].includes(d.status)).length
        const pct = catDocs.length > 0 ? Math.round((good / catDocs.length) * 100) : 0
        return { key: cat.id, label: cat.name, value: pct }
      })
      
      return { healthScore: Number(score), categoryBreakdown: breakdown }
    } catch (err) {
      console.error('Error calculating health score:', err)
      return {
        healthScore: 0,
        categoryBreakdown: countryCategories.map((c) => ({
          key: c.id,
          label: c.name || 'Unknown',
          value: 0,
        })),
      }
    }
  }, [docs, countryCategories])

  const hc = healthColor(healthScore)
  const missingCount = docs?.filter((d) => d.status === 'missing').length || 0
  const uploadedCount = docs?.filter((d) => ['uploaded', 'awaiting', 'verified'].includes(d.status)).length || 0
  const totalDocs = docs?.length || 0

  if (!countryCategories || countryCategories.length === 0) {
    return <div className="p-8 text-center"><Spinner size="lg" /></div>
  }

  return (
    <div className="p-4 sm:p-8 lg:p-10 space-y-6 animate-fade-in">
      {/* ============================================================
          HEADER
          ============================================================ */}
      <div>
        
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ink">Legal & Compliance</h1>
            <p className="text-sm text-muted mt-1 flex items-center gap-2">
              <Globe size={14} className="text-muted" />
              {countryDocData?.name || country} · {countryDocData?.totalDocuments || 0} documents
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={healthScore >= 70 ? 'success' : healthScore >= 41 ? 'warning' : 'error'} size="lg">
              <TrendingUp size={14} className="mr-1" /> {healthScore}% Health Score
            </Badge>
            <Badge variant="primary" size="lg">
              <FileCheck size={14} className="mr-1" /> {uploadedCount}/{totalDocs} Uploaded
            </Badge>
          </div>
        </div>
      </div>

      {/* ============================================================
          STATS ROW
          ============================================================ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-ink">{totalDocs}</p>
          <p className="text-xs text-muted">Total Documents</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-l-success">
          <p className="text-2xl font-bold text-success">{uploadedCount}</p>
          <p className="text-xs text-muted">Uploaded</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-l-error">
          <p className="text-2xl font-bold text-error">{missingCount}</p>
          <p className="text-xs text-muted">Missing</p>
        </Card>
        <Card className="p-4 text-center border-l-4 border-l-brand">
          <p className="text-2xl font-bold text-brand">{healthScore}%</p>
          <p className="text-xs text-muted">Readiness Score</p>
        </Card>
      </div>

      {/* ============================================================
          DOCUMENT FILTERS
          ============================================================ */}
      <DocumentFilters
        categories={countryCategories}
        statuses={statusOptions}
        searchQuery={searchQuery}
        selectedCategory={selectedCategory}
        selectedStatus={selectedStatus}
        selectedCritical={selectedCritical}
        onSearchChange={setSearchQuery}
        onFilterChange={({ category, status, critical }) => {
          setSelectedCategory(category || '')
          setSelectedStatus(status || '')
          setSelectedCritical(critical || false)
        }}
      />

      {/* ============================================================
          GOVERNANCE HEALTH SCORE (COMPACT)
          ============================================================ */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink">Governance Health Score</h3>
          <Badge variant={healthScore >= 70 ? 'success' : healthScore >= 41 ? 'warning' : 'error'}>
            {hc.label}
          </Badge>
        </div>
        <div className="w-full h-2.5 bg-neutral-100 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-500 ${hc.bar}`} style={{ width: `${healthScore}%` }} />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mt-4">
          {categoryBreakdown.map((c) => (
            <div key={c.key}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">{c.label}</span>
                <span className="text-xs font-mono text-ink">{c.value}%</span>
              </div>
              <div className="w-full h-1 bg-neutral-100 rounded-full mt-1">
                <div className={`h-full rounded-full ${healthColor(c.value).bar}`} style={{ width: `${c.value}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ============================================================
          REGULATORY IDENTIFIERS
          ============================================================ */}
      <div>
        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted mb-3">
          Regulatory Identifiers ({country})
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {regulatoryItems?.map((item) => (
            <div key={item.id} className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg border border-neutral-100">
              <span className="text-sm text-ink">{item.label}</span>
              <Badge variant={item.status === 'ok' ? 'success' : 'default'} size="sm">
                {item.value || (item.status === 'ok' ? 'Verified' : 'Pending')}
              </Badge>
            </div>
          ))}
        </div>
      </div>

      {/* ============================================================
          CRITICAL ALERT
          ============================================================ */}
      {missingCount > 0 && (
        <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-amber-800">
              {missingCount} document{missingCount > 1 ? 's' : ''} missing
            </p>
            <p className="text-xs text-amber-700">
              Upload the required documents to improve your governance score and investor readiness.
            </p>
          </div>
        </div>
      )}

      {/* ============================================================
          DOCUMENT CATEGORIES
          ============================================================ */}
      {countryCategories.map((cat) => {
        const catDocs = docs?.filter((d) => d.tier === cat.id) || []
        const filteredDocs = filterDocs(catDocs)
        const isOpen = openCats[cat.id] || false
        const isLocked = !accessibleCats.includes(cat.id)
        const CatIcon = CATEGORY_ICONS[cat.icon] || FileText
        const catUploaded = catDocs.filter(d => ['uploaded', 'awaiting', 'verified'].includes(d.status)).length
        
        return (
          <div key={cat.id} className="border border-neutral-200 rounded-xl overflow-hidden bg-white">
            {/* Category Header */}
            <button
              onClick={() => toggleCat(cat.id)}
              className="w-full flex items-center justify-between p-4 hover:bg-neutral-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center">
                  <CatIcon size={16} className="text-brand" />
                </div>
                <div className="text-left">
                  <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
                    {cat.name}
                    {isLocked && <Lock size={12} className="text-muted" />}
                  </h3>
                  <p className="text-xs text-muted">
                    {catUploaded} of {catDocs.length} uploaded
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {catUploaded === catDocs.length && catDocs.length > 0 && (
                  <CheckCircle2 size={16} className="text-success" />
                )}
                <ChevronDown size={18} className={`text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </div>
            </button>

            {/* Category Content */}
            {isOpen && (
              <div className="p-4 pt-0 border-t border-neutral-100">
                {isLocked ? (
                  <div className="flex flex-col items-center justify-center py-8 text-center">
                    <Lock size={32} className="text-muted mb-3" />
                    <p className="text-sm font-semibold text-ink">Upgrade to unlock this category</p>
                    <p className="text-xs text-muted mt-1 max-w-sm">
                      {cat.name} documents are available on higher plans.
                    </p>
                    <Button 
                      variant="primary" 
                      size="sm" 
                      className="mt-4"
                      onClick={() => onNavigateBilling?.()}
                    >
                      Upgrade Plan <ArrowRight size={14} className="ml-1" />
                    </Button>
                  </div>
                ) : filteredDocs.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText size={28} className="text-neutral-300 mx-auto mb-2" />
                    <p className="text-sm text-muted">No documents in this category</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredDocs.map((doc) => (
                      <DocumentCard
                        key={doc.id}
                        document={{
                          ...doc,
                          label: doc.label,
                          status: doc.status,
                          fileName: doc.fileName,
                          critical: doc.critical,
                          rejectReason: doc.rejectReason,
                        }}
                        onUpload={() => handleUpload(doc.id)}
                        onView={() => setPreviewDoc(doc)}
                        onRequestAgent={() => {
                          if (hasAgentAccessPlan) {
                            setSelectedAgentForHire(doc)
                            loadAvailableAgents()
                            setShowAgentMarketplace(true)
                          } else {
                            toast('Agent access requires a Growth or Enterprise plan.', 'warning')
                          }
                        }}
                        signedUrl={signedUrls[doc.id]}
                        onApprove={() => handleApproveDraft(doc.id)}
                        onReupload={() => handleReupload(doc.id)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )
      })}

      {/* ============================================================
          AGENT MARKETPLACE MODAL
          ============================================================ */}
      <Modal
        open={showAgentMarketplace}
        onClose={() => setShowAgentMarketplace(false)}
        title="Hire a Compliance Agent"
        description={`Find and hire a verified agent for "${selectedAgentForHire?.label || 'your document'}"`}
        size="lg"
        footer={
          <Button variant="secondary" onClick={() => setShowAgentMarketplace(false)}>
            Close
          </Button>
        }
      >
        <div className="space-y-4">
          {loadingAgents ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : availableAgents.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase size={32} className="text-neutral-300 mx-auto mb-3" />
              <p className="text-sm font-semibold text-ink">No agents available</p>
              <p className="text-xs text-muted mt-1">Check back later or request a specific agent</p>
              <Button 
                variant="secondary" 
                size="sm" 
                className="mt-4"
                onClick={() => {
                  setShowAgentMarketplace(false)
                  setAgentTarget({ 
                    docName: selectedAgentForHire?.label || 'Document', 
                    companyName: companyName || 'Your Company' 
                  })
                }}
              >
                Request Specific Agent
              </Button>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {availableAgents.map((agent) => (
                <div key={agent.id} className="border border-neutral-200 rounded-lg p-4 hover:border-brand transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-brand-soft rounded-full flex items-center justify-center shrink-0">
                      <span className="text-lg font-bold text-brand">{agent.name?.charAt(0) || 'A'}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="text-sm font-semibold text-ink">{agent.name}</h4>
                          <p className="text-xs text-muted">{agent.country}</p>
                        </div>
                        <Badge variant="success" size="sm">Available</Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted">
                        <span className="flex items-center gap-1">
                          <Briefcase size={12} /> {agent.experience_years || 0} years
                        </span>
                        <span className="flex items-center gap-1">
                          <Star size={12} className="text-amber-500" /> {agent.rating?.toFixed(1) || 'New'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Languages size={12} /> {agent.languages?.slice(0, 2).join(', ')}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {agent.specialties?.slice(0, 3).map((s) => (
                          <Badge key={s} variant="default" size="sm">{s}</Badge>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-muted">
                        Rate: <span className="font-semibold text-ink">
                          {agent.currency === 'NGN' ? '₦' : '$'}{agent.hourly_rate || '50'}/hr
                        </span>
                      </div>
                    </div>
                    <Button 
                      variant="primary" 
                      size="sm"
                      onClick={() => handleHireAgent(agent, selectedAgentForHire)}
                    >
                      Hire Agent
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="text-center text-xs text-muted pt-2">
            <button 
              onClick={() => {
                setShowAgentMarketplace(false)
                setAgentTarget({ 
                  docName: selectedAgentForHire?.label || 'Document', 
                  companyName: companyName || 'Your Company' 
                })
              }}
              className="text-brand hover:text-brand-dark underline"
            >
              Or request a specific agent →
            </button>
          </div>
        </div>
      </Modal>

      {/* ============================================================
          TEMPLATE LOCK MODAL
          ============================================================ */}
      {templateModal && (
        <Modal
          open={true}
          onClose={() => setTemplateModal(null)}
          title="Bizos Template Library"
          description="Access premium document templates and blueprints"
          size="sm"
          footer={
            <div className="flex gap-3 w-full">
              <Button variant="secondary" onClick={() => setTemplateModal(null)} fullWidth>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => { setTemplateModal(null); onNavigateBilling?.() }} fullWidth>
                Upgrade to Access <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          }
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-brand-soft rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText size={28} className="text-brand" />
            </div>
            <p className="text-sm text-muted">
              Upgrade your plan to access our verified library of premium enterprise templates, 
              legal frameworks, and compliance matrices.
            </p>
          </div>
        </Modal>
      )}

      {/* ============================================================
          AGENT MODAL
          ============================================================ */}
      <AgentModal
        open={!!agentTarget}
        onClose={() => setAgentTarget(null)}
        docName={agentTarget?.docName}
        companyName={agentTarget?.companyName}
        onSubmit={handleAgentRequest}
      />

      {/* ============================================================
          DOCUMENT PREVIEW MODAL
          ============================================================ */}
      <DocumentPreview
        open={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        document={previewDoc}
        signedUrl={previewDoc ? signedUrls[previewDoc.id] : null}
      />
    </div>
  )
}